// Vulnerability Operations Class
// -------------------------------
//
//

import {
  getRiskRatingScore,
  getVulnStatusScore,
  getVulnTypeScore,
} from "@/utils/vulnerabilityOperations";
import VulnerabilityClass from "./vulnerability";

class VulnerabilityOperationsClass {
  // Sort By Reference
  // ------------------
  // Sorts vulns by the reference number, e.g. alphabetical A to Z
  sortReference(listOfVulns: VulnerabilityClass[]): VulnerabilityClass[] {
    // Copy the array so we dont overwrite the old one
    const vulns = listOfVulns.slice();
    return vulns.sort(this.sortByReference);
  }

  // Sort By Highest Risk
  // ---------------------
  // Sorts vulns by the highest risk
  sortHighestRisk(listOfVulns: VulnerabilityClass[]): VulnerabilityClass[] {
    const vulns = listOfVulns.slice();
    return vulns.sort(this.sortByHighRisk);
  }

  // Sort By Lowest Risk
  // --------------------
  sortLowestRisk(listOfVulns: VulnerabilityClass[]): VulnerabilityClass[] {
    const vulns = listOfVulns.slice();
    vulns.sort(this.sortByHighRisk);
    return vulns.reverse();
  }

  // Sort By Vulnerability Type
  // ---------------------------
  // Sorts vulns by the vulnerability type
  sortVulnerabilityType(
    listOfVulns: VulnerabilityClass[]
  ): VulnerabilityClass[] {
    const vulns = listOfVulns.slice();
    return vulns.sort(this.sortByVulnType);
  }

  // Sort By Action Taken
  // ---------------------
  // Sorts vulns by the status
  sortByActionTaken(listOfVulns: VulnerabilityClass[]): VulnerabilityClass[] {
    const vulns = listOfVulns.slice();
    return vulns.sort(this.sortByAction);
  }

  /*********************************/
  /*        SORT FUNCTIONS         */
  /*********************************/

  // Sort by High Risk
  sortByHighRisk(vuln1: VulnerabilityClass, vuln2: VulnerabilityClass): number {
    const vuln1RiskRating = getRiskRatingScore(vuln1.riskRating);
    const vuln2RiskRating = getRiskRatingScore(vuln2.riskRating);

    if (vuln1RiskRating > vuln2RiskRating) {
      return -1;
    }
    if (vuln1RiskRating < vuln2RiskRating) {
      return 1;
    }
    return 0;
  }

  // Sort by reference
  sortByReference(
    vuln1: VulnerabilityClass,
    vuln2: VulnerabilityClass
  ): number {
    const extractNumber = (ref: string): number => {
      const match = ref.match(/\d+$/);
      return match ? parseInt(match[0], 10) : 0;
    };

    const ref1 = extractNumber(vuln1.reference);
    const ref2 = extractNumber(vuln2.reference);

    return ref1 - ref2;
  }

  // Sort by Vuln Type
  sortByVulnType(vuln1: VulnerabilityClass, vuln2: VulnerabilityClass): number {
    const vuln1TypeScore = getVulnTypeScore(vuln1.vulnName);
    const vuln2TypeScore = getVulnTypeScore(vuln2.vulnName);

    if (vuln1TypeScore > vuln2TypeScore) {
      return 1;
    }
    if (vuln1TypeScore < vuln2TypeScore) {
      return -1;
    }
    return 0;
  }

  // Sort by Action
  sortByAction(vuln1: VulnerabilityClass, vuln2: VulnerabilityClass): number {
    const vuln1StatusScore = getVulnStatusScore(vuln1.vulnStatus);
    const vuln2StatusScore = getVulnStatusScore(vuln2.vulnStatus);

    if (vuln1StatusScore > vuln2StatusScore) {
      return 1;
    }
    if (vuln1StatusScore < vuln2StatusScore) {
      return -1;
    }
    return 0;
  }
}
export default VulnerabilityOperationsClass;
